import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="turnstile"
// Renders the Cloudflare Turnstile widget explicitly so it survives Turbo
// navigations, tears it down before Turbo caches the page (no stale widget on
// back/forward), and guards against duplicate renders and script-load failure.
export default class extends Controller {
  static values = { sitekey: String }

  connect() {
    // Gate the form's submit button until the challenge solves. The widget div
    // and the submit button are siblings inside the <form> (not descendants of
    // this controller), so we reach the button via the closest form rather than
    // a Stimulus target.
    this.submitButton = this.element.closest("form")?.querySelector("[data-turnstile-submit]")
    this.#disableSubmit()

    this.beforeCache = () => this.#teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    // If the Cloudflare script is blocked or down, re-enable the button so the
    // user is never stranded — the server still verifies (a blank/invalid token
    // is rejected with an inline error), and passkey/OAuth remain available.
    this.#loadScript().then(() => this.#render()).catch(() => this.#enableSubmit())
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.#teardown()
  }

  #render() {
    if (this.widgetId || !window.turnstile || !this.element.isConnected) return
    // size: "flexible" makes the widget fill the parent width (min 300px) instead
    // of the fixed 300px "normal" box, matching the full-width form fields.
    this.widgetId = window.turnstile.render(this.element, {
      sitekey: this.sitekeyValue,
      theme: "auto",
      size: "flexible",
      callback: () => this.#enableSubmit(),
      "expired-callback": () => this.#disableSubmit(),
      "error-callback": () => this.#disableSubmit()
    })
  }

  #disableSubmit() {
    if (this.submitButton) this.submitButton.disabled = true
  }

  #enableSubmit() {
    if (this.submitButton) this.submitButton.disabled = false
  }

  #teardown() {
    if (this.widgetId && window.turnstile) {
      window.turnstile.remove(this.widgetId)
      this.widgetId = null
    }
  }

  #loadScript() {
    if (window.turnstile) return Promise.resolve()
    window.__turnstileLoading ||= new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      script.async = true
      script.defer = true
      script.onload = resolve
      script.onerror = () => {
        window.__turnstileLoading = null
        reject(new Error("Turnstile failed to load"))
      }
      document.head.appendChild(script)
    })
    return window.__turnstileLoading
  }
}
